import {
  FormEvent,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type KeyboardEventHandler,
} from 'react';
import { type ChatMessageItem } from '../../components/chat/ChatMessage';
import {
  hasCustomerOrderHistory,
  readShippingInfo,
} from '../../components/chat/ChatMessage/parsers/order-context';
import { type AiResponse, type SuggestedAction } from '../../types/ai';
import { sendChatStream } from '../../services/api/ai';
import { setTryOnChatSession } from '../../services/api/tryOn';
import { useOutfitWardrobeViewModel } from '../../hooks/useOutfitWardrobeViewModel';
import { OutfitWardrobePanel } from '../../components/playground/OutfitWardrobePanel';
import { PlaygroundChatHeader } from '../../components/playground/PlaygroundChatHeader';
import { PlaygroundComposer } from '../../components/playground/PlaygroundComposer';
import { PlaygroundMessageList } from '../../components/playground/PlaygroundMessageList';
import { PlaygroundNotices } from '../../components/playground/PlaygroundNotices';
import { PlaygroundRoutingDebugPanel } from '../../components/playground/PlaygroundRoutingDebugPanel';
import { PlaygroundSettingsPanel } from '../../components/playground/PlaygroundSettingsPanel';
import { PlaygroundSessionMemoryPanel } from '../../components/playground/PlaygroundSessionMemoryPanel';
import { PlaygroundSourcesPanel } from '../../components/playground/PlaygroundSourcesPanel';
import { PlaygroundShippingPanel } from '../../components/playground/PlaygroundShippingPanel';
import { useAuth } from '../../utils/authcontextt';
import { FeedbackModal } from '../../components/feedback/FeedbackModal';

import { createId, type SendMessageOptions, createWelcomeChatMessage } from './constants';
import { conversationReducer, initialConversationState } from './conversation-reducer';
import { buildChatRequest } from './request-payload';
import { buildTurnMessages } from './turn-messages';
import { createStreamCallbacks } from './stream-callbacks';
import { resolveTurnOutcome } from './turn-outcome';
import { useChatAutoScroll } from './hooks/useChatAutoScroll';
import { useRoutingReset } from './hooks/useRoutingReset';
import { useStreamStatus } from './hooks/useStreamStatus';
import { useTurnLifecycle } from './hooks/useTurnLifecycle';
import { useWelcomeSeed } from './hooks/useWelcomeSeed';
import { useModelConfigs } from './hooks/useModelConfigs';
import { usePendingImage } from './hooks/usePendingImage';
import { usePlaygroundFeedback } from './hooks/usePlaygroundFeedback';
import { useHandoffPolling } from './hooks/useHandoffPolling';
import { useIdleTimeout } from './hooks/useIdleTimeout';

const COMPOSER_MAX_HEIGHT_PX = 160;

export const PlaygroundPage = () => {
  const [message, setMessage] = useState('');
  const [selectedLang, setSelectedLang] = useState<string>('sv');
  const [conversation, dispatch] = useReducer(
    conversationReducer,
    [createWelcomeChatMessage()],
    initialConversationState,
  );
  const { messages, generation } = conversation;
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionTransitionMessage, setSessionTransitionMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => createId());
  const [sessionInfo, setSessionInfo] = useState<AiResponse['session'] | null>(null);

  // Try-on läser inloggningen ur chattsessionen. I playgrounden är det
  // "carl-lab <id>" som gör sessionen login-verifierad, samma väg som
  // livechattens kundhash.
  useEffect(() => {
    setTryOnChatSession(sessionId);
    return () => setTryOnChatSession(null);
  }, [sessionId]);

  const { activeStreamStatus, streamStatusLines, beginStream, pushStatus, endStream, resetStatus } =
    useStreamStatus();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { isStreaming, isTurnInFlight, startTurn, finishTurn } = useTurnLifecycle({
    beginStream,
    endStream,
    focusRef: textareaRef,
  });

  const {
    idleState,
    setIdleState,
    idleNoticeMessage,
    setIdleNoticeMessage,
    isSessionClosed,
    setIsSessionClosed,
    setLastActivityAt,
    clearIdleTimers,
  } = useIdleTimeout(selectedLang);

  const { startHandoffPolling, stopHandoffPolling, knownHandoffIdsRef } = useHandoffPolling({
    dispatch,
    setIsSessionClosed,
  });

  const {
    modelConfigs,
    selectedModelConfig,
    handleModelConfigChange,
    lastBrainModel,
    setLastBrainModel,
  } = useModelConfigs();

  const { pendingImage, setPendingImage, isPreparingImage, imageInputRef, handleImageFileChange } =
    usePendingImage({ setErrorMessage });

  const { feedbackModal, handleAdminFeedback, handleCloseFeedbackModal, handleSubmitFeedback } =
    usePlaygroundFeedback(messages);

  const { user } = useAuth();
  const { resetRouting, isResettingRouting } = useRoutingReset({
    sessionId,
    dispatch,
    setSessionInfo,
    setSessionTransitionMessage,
    setErrorMessage,
  });
  const showStreamDebug = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('devtools') === '1';
  }, []);

  const latestAssistantMessage = useMemo(() => {
    return [...messages].reverse().find((item) => item.role === 'assistant');
  }, [messages]);

  const wardrobeViewModel = useOutfitWardrobeViewModel(messages);

  const latestShippingInfo = useMemo(
    () => readShippingInfo(latestAssistantMessage?.orderContext),
    [latestAssistantMessage],
  );

  const customerHasOrderHistory = useMemo(
    () => hasCustomerOrderHistory(latestAssistantMessage?.orderContext),
    [latestAssistantMessage],
  );

  const { scrollContainerRef, handleMessageScroll } = useChatAutoScroll({
    messages,
    isStreaming,
  });

  useEffect(() => {
    const field = textareaRef.current;
    if (!field) return;
    // Nollställningen FÖRST. Utan den kan fältet bara växa, aldrig krympa igen.
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [message]);

  const resetConversationState = ({
    nextSessionId = createId(),
    nextSessionInfo = null,
    nextMessages,
    transitionMessage = null,
  }: {
    nextSessionId?: string;
    nextSessionInfo?: AiResponse['session'] | null;
    nextMessages?: ChatMessageItem[];
    transitionMessage?: string | null;
  } = {}) => {
    dispatch({
      type: 'conversation/reset',
      messages: nextMessages ?? [createWelcomeChatMessage()],
    });
    setErrorMessage(null);
    setPendingImage(null);
    setSessionTransitionMessage(transitionMessage);
    setSessionId(nextSessionId);
    setSessionInfo(nextSessionInfo);
    setIdleState('active');
    setIdleNoticeMessage(null);
    setIsSessionClosed(false);
    setLastActivityAt(Date.now());
    resetStatus();
    stopHandoffPolling();
    knownHandoffIdsRef.current = new Set();
    clearIdleTimers();
  };

  useWelcomeSeed({
    sessionId,
    language: selectedLang,
    generation,
    dispatch,
  });

  const sendMessageToAgent = async (input: string, options?: SendMessageOptions) => {
    if (isStreaming || isTurnInFlight() || idleState === 'timed_out' || isSessionClosed) {
      return;
    }

    const imageToSend = pendingImage;
    const turn = buildTurnMessages({
      input,
      language: selectedLang,
      image: imageToSend,
      suggestedAction: options?.suggestedAction,
    });
    if (!turn) {
      return;
    }
    const { trimmed, structuredSuggestion, userMessage, assistantMessage } = turn;
    const assistantMessageId = assistantMessage.id;

    const conversationHistory = conversationRef.current.messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
    const clientRequestId = createId();
    const turnGeneration = conversationRef.current.generation;
    const isStaleTurn = () => conversationRef.current.generation !== turnGeneration;

    setMessage('');
    setPendingImage(null);
    setErrorMessage(null);
    setSessionTransitionMessage(null);
    setLastActivityAt(Date.now());

    dispatch({ type: 'turn/started', generation: turnGeneration, userMessage, assistantMessage });
    startTurn();

    try {
      const payload = buildChatRequest({
        message: trimmed,
        sessionId,
        language: selectedLang,
        clientRequestId,
        conversationHistory,
        image: imageToSend ?? undefined,
        suggestedAction: options?.suggestedAction,
        structuredSuggestion,
        modelConfig:
          selectedModelConfig !== 'standard' && modelConfigs.length > 0
            ? selectedModelConfig
            : undefined,
        customerId: user?.id?.trim() || undefined,
      });

      const response = await sendChatStream(
        payload,
        createStreamCallbacks({
          dispatch,
          generation: turnGeneration,
          messageId: assistantMessageId,
          pushStatus,
          isStaleTurn,
          onHandoffRequested: () => startHandoffPolling(sessionId),
        }),
      );

      if (isStaleTurn()) {
        return;
      }

      if (response.sessionId && response.sessionId !== sessionId) {
        setSessionId(response.sessionId);
      }
      setSessionInfo((prev) => response.session ?? prev);

      const outcome = resolveTurnOutcome(response, {
        sessionId,
        assistantMessage,
        fallbackLanguage: selectedLang,
      });

      if (outcome.kind === 'customer-switched') {
        if (isStaleTurn()) {
          return;
        }
        resetConversationState({
          nextSessionId: outcome.nextSessionId,
          nextSessionInfo: outcome.session,
          nextMessages: [createWelcomeChatMessage(), outcome.restartedMessage],
          transitionMessage:
            'Kund byttes under verifieringen. Tidigare chatt rensades och en ny verifierad session startades.',
        });
        return;
      }

      if (outcome.verificationUpgraded) {
        setSessionTransitionMessage(
          'Sessionen uppgraderades till verifierad kund utan att chatten startades om.',
        );
      }

      if (isStaleTurn()) {
        return;
      }

      if (outcome.brainModel) {
        setLastBrainModel(outcome.brainModel);
      }

      dispatch({
        type: 'turn/completed',
        generation: turnGeneration,
        messageId: assistantMessageId,
        response,
        fallbackLanguage: selectedLang,
      });

      if (outcome.handoffSessionId) {
        startHandoffPolling(outcome.handoffSessionId);
      }

      if (outcome.endChat) {
        setIsSessionClosed(true);
        stopHandoffPolling();
      }
    } catch (error) {
      if (isStaleTurn()) {
        return;
      }
      const messageText =
        error instanceof Error ? error.message : 'Kunde inte kontakta agenten. Försök igen senare.';

      setErrorMessage(messageText);
      dispatch({
        type: 'turn/failed',
        generation: turnGeneration,
        messageId: assistantMessageId,
        message: messageText,
      });
    } finally {
      finishTurn({ refocus: !isStaleTurn() });
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendMessageToAgent(message);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    if (!disabledSend) {
      void sendMessageToAgent(message);
    }
  };

  const startNewChat = () => {
    resetConversationState();
  };

  const handleSuggestedActionClick = (action: SuggestedAction) => {
    dispatch({ type: 'suggestedActions/cleared' });
    void sendMessageToAgent(action.value, { suggestedAction: action });
  };

  const handleEmailDraftResolved = (
    resolvedMessage: string,
    status: 'confirmed' | 'already_sent' | 'cancelled',
    sessionClosed?: boolean,
  ) => {
    const finalStatus = status === 'cancelled' ? 'cancelled' : 'sent';
    dispatch({
      type: 'emailDraft/resolved',
      status: finalStatus,
      note:
        resolvedMessage.trim().length > 0
          ? {
              id: createId(),
              role: 'assistant',
              content: resolvedMessage,
              createdAt: new Date().toISOString(),
              status: 'success',
            }
          : undefined,
    });
    setLastActivityAt(Date.now());
    if (sessionClosed) {
      setIsSessionClosed(true);
      stopHandoffPolling();
    }
  };

  const isChatTimedOut = idleState === 'timed_out';
  const disabledSend =
    isStreaming ||
    isChatTimedOut ||
    isSessionClosed ||
    isPreparingImage ||
    (message.trim().length === 0 && !pendingImage);

  return (
    <section className="flex h-[calc(100dvh-3rem)] max-h-[calc(100dvh-3rem)] min-h-0 flex-1 flex-col gap-6 overflow-hidden">
      <header>
        <h2 className="text-3xl font-display text-brand-blue">Agent Playground</h2>
        <p className="text-sm text-text-grey">
          Testa chatten end-to-end, verifiera copy och följ vilka källor/sessionsdata som används.
        </p>
      </header>

      <div className="grid flex-1 min-h-0 gap-5 xl:grid-cols-[minmax(0,2.6fr)_minmax(0,1fr)]">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-black/10 bg-brand-beige-tint-1 shadow-sm">
          <PlaygroundChatHeader
            hasMessages={messages.length > 0}
            isResettingOrchestration={isResettingRouting}
            onEmergencyRoutingReset={resetRouting}
            onResetConversation={startNewChat}
          />

          <PlaygroundMessageList
            scrollContainerRef={scrollContainerRef}
            onMessageScroll={handleMessageScroll}
            messages={messages}
            sessionId={sessionId}
            language={selectedLang}
            channel="playground"
            onSuggestedActionClick={handleSuggestedActionClick}
            onAdminFeedback={handleAdminFeedback}
            onEmailDraftResolved={handleEmailDraftResolved}
            isStreaming={isStreaming}
            showStreamDebug={showStreamDebug}
            streamStatusLines={streamStatusLines}
            activeStreamStatus={activeStreamStatus}
          />

          <PlaygroundNotices
            language={selectedLang}
            errorMessage={errorMessage}
            isSessionClosed={isSessionClosed}
            idleState={idleState}
            idleNoticeMessage={idleNoticeMessage}
            sessionTransitionMessage={sessionTransitionMessage}
            onStartNewChat={startNewChat}
          />

          <PlaygroundComposer
            message={message}
            onMessageChange={setMessage}
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            textareaRef={textareaRef}
            pendingImage={pendingImage}
            onClearPendingImage={() => setPendingImage(null)}
            imageInputRef={imageInputRef}
            onImageFileChange={(event) => {
              void handleImageFileChange(event);
            }}
            isPreparingImage={isPreparingImage}
            isFieldDisabled={isStreaming || isChatTimedOut || isSessionClosed}
            isSendDisabled={disabledSend}
            isStreaming={isStreaming}
          />
        </div>

        <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto pr-1">
          <PlaygroundSettingsPanel
            selectedLang={selectedLang}
            onSelectLang={(code) => {
              setSelectedLang(code);
              resetConversationState();
            }}
            modelConfigs={modelConfigs}
            selectedModelConfig={selectedModelConfig}
            onModelConfigChange={handleModelConfigChange}
            lastBrainModel={lastBrainModel}
          />

          <PlaygroundSessionMemoryPanel
            sessionInfo={sessionInfo}
            sessionId={sessionId}
            messagesLength={messages.length}
            hasCustomerOrderHistory={customerHasOrderHistory}
          />

          {latestAssistantMessage?.routing ? (
            <PlaygroundRoutingDebugPanel routing={latestAssistantMessage.routing} />
          ) : null}

          <OutfitWardrobePanel viewModel={wardrobeViewModel} />

          {latestAssistantMessage?.sources?.length ? (
            <PlaygroundSourcesPanel sources={latestAssistantMessage.sources} />
          ) : null}

          {latestShippingInfo ? (
            <PlaygroundShippingPanel latestShippingInfo={latestShippingInfo} />
          ) : null}
        </aside>
      </div>
      {feedbackModal ? (
        <FeedbackModal
          preselectedType={feedbackModal.type}
          conversationId={sessionId}
          messageId={feedbackModal.messageId}
          userMessage={feedbackModal.userMessage}
          assistantAnswer={feedbackModal.assistantAnswer}
          onSubmit={handleSubmitFeedback}
          onClose={handleCloseFeedbackModal}
        />
      ) : null}
    </section>
  );
};
